import React from 'react';
import PropTypes from 'prop-types';
import { Send, Loader2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { DropdownMenuItem } from 'src/components/ui/dropdown-menu';
import { useAssignTest } from 'src/services/api/mutations/candidateMutations';
import { useToast } from 'src/hooks/use-toast';
import { cn } from 'src/lib/utils';
import { getToneButtonClasses } from 'src/lib/statusStyles';
import { registerAction } from './actionRegistry';

// 'blue' — communication/outreach, same scheme senior_level_flow's own
// send_contact_link uses — see actionColors.js. Only applied to the
// menuItem branch below; the button branch keeps its solid `variant="default"`
// look on purpose (a bulk-send CTA that's already appropriately prominent —
// softening it into the tonal look would read as less important, not more).
const tone = getToneButtonClasses('blue');

// "Assign Test" — sends the flow's test to the given candidates. Reuses the
// existing assign-test endpoint/mutation as-is (the same one the old
// RequirementPipeline "Send test link" bulk action called): POST /internal/
// assign-test with { mapping_ids, opportunityId }. mapping_ids are the
// candidates' mapping ids, not their candidate_id — extraction from
// whatever row shape the caller has is the caller's job, this component
// just needs the ids themselves.
//
// `label` defaults to "Assign Test" but can be overridden — this action is
// resolved and rendered generically off the flow-statuses API's
// manual_actions (e.g. { key: 'assign_test', label: 'Assign Test' }), so a
// different flow could configure a different label for the same key
// without a frontend change.
const AssignTestAction = ({
  mappingIds,
  opportunityId,
  onSuccess,
  label,
  renderAs,
}) => {
  const { toast } = useToast();
  const assignTestMutation = useAssignTest();

  const disabled = mappingIds.length === 0 || assignTestMutation.isPending;

  const handleClick = async () => {
    if (mappingIds.length === 0) {
      toast({ message: 'Select at least one candidate', variant: 'warning' });
      return;
    }
    try {
      const response = await assignTestMutation.mutateAsync({
        mapping_ids: mappingIds,
        opportunityId,
      });

      const successCount = response?.data?.success_count ?? mappingIds.length;
      const failureCount = response?.data?.failure_count ?? 0;

      if (failureCount === 0) {
        toast({
          message: `Test assigned to ${successCount} candidate${successCount === 1 ? '' : 's'}`,
          variant: 'success',
        });
      } else if (successCount === 0) {
        toast({
          message: `Failed to assign test to ${failureCount} candidate${failureCount === 1 ? '' : 's'}`,
          variant: 'destructive',
        });
      } else {
        toast({
          message: `Test assigned: ${successCount} succeeded, ${failureCount} failed`,
          variant: 'destructive',
        });
      }
      onSuccess(response);
    } catch (error) {
      toast({
        message: error.message || 'Failed to assign test',
        variant: 'destructive',
      });
    }
  };

  if (renderAs === 'menuItem') {
    return (
      <DropdownMenuItem
        onClick={handleClick}
        disabled={disabled}
        // Tinted to this action's own color (tone.text) — see actionColors.js
        // and GenericFlowAction.jsx's own menuItem treatment (including its
        // comment on why `focus:${tone.text}` is required, not just the
        // background), which this mirrors for consistency across every
        // kebab in this feature.
        className={cn(
          'gap-2',
          tone.text,
          `focus:${tone.text}`,
          tone.hoverBg,
          tone.hoverBg.replace(/hover:/g, 'focus:')
        )}
      >
        <Send className="h-4 w-4" />
        {label}
      </DropdownMenuItem>
    );
  }

  return (
    <Button
      variant="default"
      size="sm"
      onClick={handleClick}
      disabled={disabled}
      className="flex items-center gap-1"
    >
      {assignTestMutation.isPending ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Assigning...
        </>
      ) : (
        <>
          <Send className="h-4 w-4" />
          {label}
        </>
      )}
    </Button>
  );
};

AssignTestAction.propTypes = {
  mappingIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
  label: PropTypes.string,
  renderAs: PropTypes.oneOf(['button', 'menuItem']),
};

AssignTestAction.defaultProps = {
  mappingIds: [],
  opportunityId: undefined,
  onSuccess: () => {},
  label: 'Assign Test',
  renderAs: 'button',
};

registerAction({
  stateMachineKey: 'default',
  actionKey: 'assign_test',
  component: AssignTestAction,
});

export default AssignTestAction;
